import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Snackbar, Stack, Switch, TextField, Tooltip, Typography} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CategorySelect from './CategorySelect';
import FaqWritingTips from './FaqWritingTips';
import styles from './styles/faq.module.css';

const MAX_TITLE = 100;
const MAX_QUESTION = 200;
const MAX_ANSWER = 2000;

const fieldLabelSx = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--gray-darker, #222)',
    mb: 0.5
};

const FieldLabel = ({ text, required }) => (
    <Typography sx={fieldLabelSx}>
        {text}{' '}
        <span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>
            ({required ? '필수' : '선택'})
        </span>
    </Typography>
);

const EMPTY_BASELINE = {
    title: '',
    question: '',
    answer: '',
    categoryMainId: null,
    categorySubId: null,
    changeReason: '',
    isActive: true
};

const buildBaselineFromDetail = (detail) => ({
    title: detail?.title || '',
    question: detail?.question || '',
    answer: detail?.answer || '',
    categoryMainId: detail?.category_main_id ?? detail?.categoryMainId ?? null,
    categorySubId: detail?.category_sub_id ?? detail?.categorySubId ?? null,
    changeReason: detail?.change_reason || detail?.changeReason || '',
    isActive: detail?.is_active !== undefined ? detail.is_active : (detail?.isActive !== undefined ? detail.isActive : true)
});

export default function FaqEditor({
    mode, form, detailResponse, categoryTree, saving, saveErrorResponse,
    onChangeForm, onChangeCategory, onSubmit
}) {
    const navigate = useNavigate();
    const isEdit = mode === 'edit';

    const titleOver = (form.title?.length || 0) > MAX_TITLE;
    const questionOver = (form.question?.length || 0) > MAX_QUESTION;
    const answerOver = (form.answer?.length || 0) > MAX_ANSWER;

    const conflictMessage = useMemo(() => {
        if (!saveErrorResponse) return null;
        const isConflict = saveErrorResponse.status === 409 || saveErrorResponse.code === 409;
        if (!isConflict) return null;
        return saveErrorResponse.detail || saveErrorResponse.message || 'AI가 학습 중이라 잠시 후 다시 시도해주세요.';
    }, [saveErrorResponse]);

    const [conflictOpen, setConflictOpen] = useState(false);
    useEffect(() => {
        if (conflictMessage) setConflictOpen(true);
    }, [conflictMessage, saveErrorResponse]);

    const validationError = useMemo(() => {
        if (!form.title?.trim()) return '문서 제목을 입력하세요.';
        if (!form.question?.trim()) return '질문을 입력하세요.';
        if (!form.answer?.trim()) return '답변을 입력하세요.';
        if (titleOver) return `문서 제목은 ${MAX_TITLE}자 이내로 작성해주세요.`;
        if (questionOver) return `질문은 ${MAX_QUESTION}자 이내로 작성해주세요.`;
        if (answerOver) return `답변은 ${MAX_ANSWER}자 이내로 작성해주세요.`;
        return null;
    }, [form, titleOver, questionOver, answerOver]);

    const handleChange = (e) => {
        onChangeForm({ name: e.target.name, value: e.target.value });
    };

    const handleActiveToggle = (e) => {
        onChangeForm({ name: 'isActive', value: e.target.checked });
    };

    const baseline = useMemo(() => {
        if (!isEdit) return EMPTY_BASELINE;
        if (!detailResponse) return null;
        return buildBaselineFromDetail(detailResponse);
    }, [isEdit, detailResponse]);

    const isDirty = useMemo(() => {
        if (!baseline) return false;
        return JSON.stringify(baseline) !== JSON.stringify(form);
    }, [baseline, form]);

    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

    const handleCancelClick = () => {
        if (isDirty) {
            setCancelConfirmOpen(true);
        } else {
            navigate('/admin/faq');
        }
    };

    const handleConfirmLeave = () => {
        setCancelConfirmOpen(false);
        navigate('/admin/faq');
    };

    return (
        <Box className={styles.wrapper}>
            <Box className={styles.inner}>
                <Box className={styles.header}>
                <h2 className={styles.title}>{isEdit ? 'FAQ 수정' : 'FAQ 신규 등록'}</h2>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                    <FormControlLabel
                        control={<Switch checked={!!form.isActive} onChange={handleActiveToggle} />}
                        label={form.isActive ? '활성화' : '비활성화'}
                        sx={{ mr: 0 }}
                    />
                    <Tooltip
                        title={<>활성화된 FAQ만 챗봇 응답에 사용됩니다.<br />비활성화 시 응답에서 제외됩니다.</>}
                        arrow
                        placement='top'
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#888', cursor: 'help' }} />
                    </Tooltip>
                </Stack>
            </Box>

            <FaqWritingTips />

            <Box className={styles.editorWrapper}>
                <Box>
                    <FieldLabel text='질문' required />
                    <TextField
                        name='question'
                        placeholder='사용자가 묻는 질문을 입력해 주세요.'
                        value={form.question}
                        onChange={handleChange}
                        error={questionOver}
                        fullWidth
                        autoComplete='off'
                    />
                    <Box className={styles.charCounter + (questionOver ? ' ' + styles.charCounterOver : '')}>
                        {(form.question?.length || 0)} / {MAX_QUESTION}
                    </Box>
                </Box>

                <Box>
                    <FieldLabel text='답변' required />
                    <TextField
                        name='answer'
                        multiline
                        minRows={2}
                        placeholder='답변을 작성해 주세요.'
                        value={form.answer}
                        onChange={handleChange}
                        error={answerOver}
                        fullWidth
                        autoComplete='off'
                    />
                    <Box className={styles.charCounter + (answerOver ? ' ' + styles.charCounterOver : '')}>
                        {(form.answer?.length || 0)} / {MAX_ANSWER}
                    </Box>
                </Box>

                <Box className={styles.editorRow}>
                    <CategorySelect
                        mainId={form.categoryMainId}
                        subId={form.categorySubId}
                        tree={categoryTree}
                        onChange={onChangeCategory}
                        required={false}
                    />
                </Box>

                <Box>
                    <FieldLabel text='문서 제목' required />
                    <TextField
                        name='title'
                        placeholder='문서로 등록될 때 사용할 제목을 입력해 주세요.'
                        value={form.title || ''}
                        onChange={handleChange}
                        error={titleOver}
                        fullWidth
                        autoComplete='off'
                    />
                    <Box className={styles.charCounter + (titleOver ? ' ' + styles.charCounterOver : '')}>
                        {(form.title?.length || 0)} / {MAX_TITLE}
                    </Box>
                </Box>

                <Box>
                    <FieldLabel text='수정 사유' required={false} />
                    <TextField
                        name='changeReason'
                        placeholder='변경 사유를 입력해 주세요.'
                        value={form.changeReason || ''}
                        onChange={handleChange}
                        fullWidth
                        autoComplete='off'
                    />
                </Box>

                {validationError && (
                    <Box sx={{ color: '#d32f2f', fontSize: 13 }}>{validationError}</Box>
                )}
                {saveErrorResponse && !conflictMessage && (
                    <Box sx={{ color: '#d32f2f', fontSize: 13 }}>
                        저장에 실패했습니다. 잠시 후 다시 시도하세요.
                    </Box>
                )}

                <Stack direction='row' spacing='2px' className={styles.footerActions}>
                    <Button
                        variant='text'
                        onClick={handleCancelClick}
                        disabled={saving}
                        sx={{ '&:hover': { backgroundColor: 'transparent' } }}
                    >
                        취소
                    </Button>
                    <Button
                        variant='contained'
                        onClick={onSubmit}
                        disabled={!!validationError || saving}
                        sx={{ '&.Mui-disabled': { color: '#fff' } }}
                    >
                        {saving ? <CircularProgress size={18} color='inherit' /> : (isEdit ? '수정' : '등록')}
                    </Button>
                </Stack>
                </Box>
            </Box>
            <Snackbar
                open={conflictOpen}
                autoHideDuration={4000}
                onClose={() => setConflictOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setConflictOpen(false)}
                    severity='warning'
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {conflictMessage}
                </Alert>
            </Snackbar>
            <Dialog
                open={cancelConfirmOpen}
                onClose={() => setCancelConfirmOpen(false)}
                aria-labelledby='faq-cancel-confirm-title'
            >
                <DialogTitle id='faq-cancel-confirm-title'>
                    작성을 취소하시겠습니까?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        지금 나가면 작성 중인 내용이 모두 사라집니다.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelConfirmOpen(false)} variant='text'>
                        계속 작성
                    </Button>
                    <Button onClick={handleConfirmLeave} variant='contained' color='error' autoFocus>
                        나가기
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
